import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button, Input, Card, Logo } from '../components/common';
import { resetPassword } from '../services/api/auth.api';
import { useMutation } from '@tanstack/react-query';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordReadonly, setPasswordReadonly] = useState(true);
  const [confirmPasswordReadonly, setConfirmPasswordReadonly] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);

  const resetPasswordMutation = useMutation({
    mutationFn: ({ token, password }: { token: string; password: string }) =>
      resetPassword(token, password),
    onSuccess: () => {
      setIsSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    },
  });

  useEffect(() => {
    if (!token) {
      navigate('/find-account');
    }
  }, [token, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      return;
    }

    if (password !== confirmPassword) {
      return;
    }

    resetPasswordMutation.mutate({ token, password });
  };

  const passwordMatch = password === confirmPassword && confirmPassword.length > 0;
  const passwordMismatch =
    password !== confirmPassword && confirmPassword.length > 0;

  if (!token) {
    return null;
  }

  if (isSuccess) {
    return (
      <div className="hell-scope relative min-h-screen flex items-center justify-center bg-[var(--bg)] p-4">
        <motion.div
          className="relative w-full max-w-md"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Card variant="glass">
            <div className="text-center py-8">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                className="inline-flex items-center justify-center w-20 h-20 bg-[var(--accent)]/20 rounded-full mb-6"
              >
                <CheckCircle className="w-12 h-12 text-[var(--accent)]" />
              </motion.div>
              <h2 className="text-3xl font-bold text-[var(--text)] mb-4">
                비밀번호 재설정 완료
              </h2>
              <p className="text-[var(--text-secondary)] mb-8">
                비밀번호가 성공적으로 재설정되었습니다.
                <br />
                잠시 후 로그인 페이지로 이동합니다.
              </p>
              <Link to="/login">
                <Button variant="primary" size="lg" fullWidth>
                  로그인하러 가기
                </Button>
              </Link>
            </div>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="hell-scope relative min-h-screen flex items-center justify-center bg-[var(--bg)] p-4">
      {/* 배경 장식 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-[var(--accent)]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[var(--accent)]/5 rounded-full blur-3xl"></div>
      </div>

      <motion.div
        className="relative w-full max-w-md"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card variant="glass">
          {/* 로고 */}
          <div className="flex justify-center mb-8">
            <Logo size="lg" />
          </div>

          {/* 제목 */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-[var(--text)] mb-2">비밀번호 재설정</h2>
            <p className="text-[var(--text-secondary)]">새로운 비밀번호를 입력해주세요</p>
          </div>

          {/* 에러 메시지 */}
          {resetPasswordMutation.isError && (
            <motion.div
              className="mb-6 p-4 bg-[var(--danger)]/20 border border-[var(--danger)] rounded-btn flex items-center gap-2"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <AlertCircle size={18} className="text-[var(--danger)] flex-shrink-0" />
              <p className="text-[var(--danger)] text-sm font-semibold">
                {(resetPasswordMutation.error as any)?.response?.data?.error?.message ||
                  '비밀번호 재설정에 실패했습니다. 링크가 만료되었을 수 있습니다.'}
              </p>
            </motion.div>
          )}

          {/* 폼 */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                label="새 비밀번호"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setPasswordReadonly(false)}
                required
                autoComplete="new-password"
                readOnly={passwordReadonly}
                leftIcon={<Lock className="w-5 h-5" />}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-[42px] text-[var(--text-muted)] hover:text-[var(--text)] transition-colors"
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>

            <div className="relative">
              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                label="비밀번호 확인"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onFocus={() => setConfirmPasswordReadonly(false)}
                required
                autoComplete="new-password"
                readOnly={confirmPasswordReadonly}
                leftIcon={<Lock className="w-5 h-5" />}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-[42px] text-[var(--text-muted)] hover:text-[var(--text)] transition-colors"
              >
                {showConfirmPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>

            {/* 비밀번호 일치 확인 */}
            {passwordMismatch && (
              <motion.p
                className="text-[var(--danger)] text-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                비밀번호가 일치하지 않습니다
              </motion.p>
            )}
            {passwordMatch && (
              <motion.p
                className="text-[var(--accent)] text-sm flex items-center gap-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <CheckCircle className="w-4 h-4" />
                비밀번호가 일치합니다
              </motion.p>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              isLoading={resetPasswordMutation.isPending}
              disabled={!passwordMatch || password.length < 8}
            >
              비밀번호 재설정
            </Button>
          </form>

          {/* 로그인 링크 */}
          <div className="mt-8 text-center">
            <Link
              to="/login"
              className="text-[var(--accent)] hover:opacity-80 font-semibold transition-colors text-sm"
            >
              ← 로그인으로 돌아가기
            </Link>
          </div>
        </Card>

        {/* 하단 텍스트 */}
        <motion.p
          className="text-center text-[var(--text-muted)] text-sm mt-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          헬스를 RPG처럼! 운동할수록 강해지세요 💪
        </motion.p>
      </motion.div>
    </div>
  );
}

